/* eslint-disable no-nested-ternary */
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { DASHBOARD_PATH } from 'config';
import { DefaultRootStateProps, NavItemType } from 'types';
import axios from 'utils/axios';
import { createMerchantList, getNewRouteType, retryAsync } from 'utils/helpers';
import { getActiveFieldsMock } from './mocks/getActiveFieldsMock';
import { RouteObject } from 'react-router-dom';
import MainRoutes from 'routes/MainRoutes';
import { AvailableMenuType, MerchantType } from 'types/components/shared';
import { dispatch } from 'store';
import { openSnackbar } from './snackbar';

const initialState: DefaultRootStateProps['auth'] = {
    userName: undefined,
    error: null,
    loading: false,
    user: null,
    merchants: null,
    fatherMerchant: null,
    vendor: null,
    activeFields: null,
    menuDetails: [],
    filteredRoutes: [],
    defaultPath: DASHBOARD_PATH,
    idUser: null,
    /** default merchant, global selection */
    defaultSelectedMerchant: null,
    tenantId: null,
    profileId: 0,
    profileType: '',
    isProvider: false,
    termsAndConditions: false
};

const slice = createSlice({
    name: 'auth',
    initialState,
    extraReducers(builder) {
        builder
            .addCase(getUserMulticatalog.pending, (state) => {
                state.loading = true;
            })
            .addCase(getUserMulticatalog.rejected, (state) => {
                state.loading = false;
            })
            .addCase(getUserMulticatalog.fulfilled, (state, action) => {
                const { merchant, provider, profile, id } = action.payload.response;

                const menuDetails = profile?.menuDetails;
                state.idUser = id;
                // Filtered Routes and Default Path
                if (profile) {
                    state.menuDetails = profile.menuDetails;
                    state.profileId = profile.id;
                    state.profileType = profile.type;
                }

                const menuChildren =
                    menuDetails?.flatMap((item: AvailableMenuType) =>
                        item.children.map((child: AvailableMenuType) => getNewRouteType(child.type))
                    ) || [];

                const filteredRoutes: RouteObject[] = MainRoutes.children!.filter((route) =>
                    menuChildren.some((menuItem: NavItemType) => route!.path!.includes(`/${menuItem}`))
                );

                state.filteredRoutes = filteredRoutes;
                const firstFilteredRoute = filteredRoutes[0];
                const defaultRoute = filteredRoutes.find((route: RouteObject) => route.path === DASHBOARD_PATH) || firstFilteredRoute;

                state.defaultPath = defaultRoute?.path || '/';

                // Vendor y Merchants
                if (provider) {
                    state.isProvider = true;
                    state.termsAndConditions = action.payload.response.termsAndConditions;
                }
                const vendorSelected = provider?.idProvider;
                const { name, status, merchantId, fatherMerchantId } = merchant;

                const vendorMerchant = {
                    name,
                    status,
                    merchantId,
                    isFather: !fatherMerchantId
                };

                const defaultMerchantId = Number(localStorage.getItem('defaultMerchantId'));
                let defaultMerchant: MerchantType | undefined;

                if (merchant) {
                    const newMerchants: MerchantType[] =
                        vendorSelected && vendorMerchant.merchantId !== 1 ? [vendorMerchant] : createMerchantList(merchant);

                    state.merchants = [...newMerchants];

                    // FIND FATHER MERCHANT
                    const fatherMerchant = newMerchants.find((merch) => merch.isFather) || null;

                    state.fatherMerchant = fatherMerchant;

                    if (defaultMerchantId) {
                        defaultMerchant = newMerchants.find((item) => item.merchantId === defaultMerchantId && Boolean(item.status));
                    }

                    state.defaultSelectedMerchant = defaultMerchant ?? fatherMerchant;
                }
                if (vendorSelected) {
                    state.vendor = { idMerchant: merchantId, idProvider: vendorSelected };
                }
                state.loading = false;
            });
        // ACTIVE FIELDS
        builder
            .addCase(getActiveFields.pending, (state) => {
                state.loading = true;
            })
            .addCase(getActiveFields.fulfilled, (state, action) => {
                state.activeFields = action.payload;
                state.loading = false;
            })
            .addCase(getTenantId.pending, (state) => {
                state.loading = true;
            })
            .addCase(getTenantId.rejected, (state) => {
                state.loading = false;
            })
            .addCase(getTenantId.fulfilled, (state, action) => {
                state.loading = false;
                state.tenantId = action.payload;

                localStorage.setItem('tenantId', action.payload);
            })
            .addCase(getTermsandProvider.pending, (state) => {
                state.loading = true;
            })
            .addCase(getTermsandProvider.rejected, (state) => {
                state.loading = false;
            })
            .addCase(getTermsandProvider.fulfilled, (state, action) => {
                state.loading = false;
                const expires = new Date();
                expires.setTime(expires.getTime() + 1 * 86400000);
                document.cookie = `isProvider=${encodeURIComponent(
                    action.payload.response.isProvider
                )}; expires=${expires.toUTCString()}; path=/`;
                document.cookie = `termsAndConditions=${encodeURIComponent(
                    action.payload.response.termsAndConditions
                )}; expires=${expires.toUTCString()}; path=/`;
            });
    },
    reducers: {
        logoutState(state) {
            state = initialState;
        },
        updateTermsAndConditionsStatus(state) {
            state.termsAndConditions = true;
            state.loading = false;
        },
        // HAS ERROR
        hasError(state, action) {
            state.error = action.payload;
        },
        setAuthData(state, action) {
            state.userName = action.payload;
        },
        cleanActiveFields(state, action) {
            state.activeFields = null;
            state.vendor = null;
        },
        updateGlobalMerchant(state, action: PayloadAction<MerchantType>) {
            state.defaultSelectedMerchant = action.payload;
            localStorage.setItem('defaultMerchantId', `${action.payload.merchantId}`);
        }
    }
});

export const getUserMulticatalog = createAsyncThunk(`${slice.name}/getMerchants`, async (user: string | null) => {
    const getProfile = async (): Promise<any> => {
        const response = await axios.get(`/loolu/api/profile/user-multicatalog`, {
            params: {
                user
            }
        });

        const email = (response.data.response.email ?? '').toLowerCase();
        const profile = response.data.response.profile;
        const userEmail = user?.toLowerCase();

        if (email !== userEmail) {
            throw new Error('Invalid email');
        }

        if (!profile) {
            throw new Error('Invalid profile');
        }

        return response.data;
    };

    const response = await retryAsync(getProfile, 3);

    return response;
});

export const getActiveFields = createAsyncThunk(
    `${slice.name}/getActiveFields`,
    async ({ idMerchant, idProvider }: { idMerchant: number; idProvider: number }) => {
        let result;
        try {
            const response = await axios.get(`/loolu/api/product/activeFields`, {
                params: {
                    idMerchant,
                    idProvider
                }
            });

            result = response.data.response;
        } catch (err) {
            result = getActiveFieldsMock;
        }

        return result;
    }
);

export const getTenantId = createAsyncThunk<string, void>(`${slice.name}/getTenantId`, async () => {
    const maxRetries = 2;
    const fetchTenantId = async (attempt: number): Promise<string> => {
        try {
            const host = window.location.hostname;
            const tenant = host.split('.')[0];
            const response = await axios.get(`/loolu/api/auth/tenants/${tenant}`, {});

            const id = response.data.id || null;
            return id;
        } catch (err) {
            if (attempt < maxRetries) {
                return await fetchTenantId(attempt + 1);
            } else {
                dispatch(
                    openSnackbar({
                        message: 'An error ocurred fetching tenantId, contact your administrator.',
                        variant: 'error'
                    })
                );
                return '';
            }
        }
    };

    return await fetchTenantId(0);
});

export const getTermsandProvider = createAsyncThunk(`${slice.name}/getTermsandProvider`, async () => {
    const response = await axios.get(`/loolu/api/termsandconditions/accepted`, {});

    return response.data;
});

export const { cleanActiveFields, updateGlobalMerchant, updateTermsAndConditionsStatus, logoutState } = slice.actions;

// Reducer
export default slice.reducer;
